import { useMemo } from 'react'
import { Controller, useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  Button,
  Checkbox,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@clouway/ui'

// ~40 analog ports, the 5 input types and 5 units — keys into the feature
// i18n enums (config.analogPorts / analogInputTypes / analogInputUnits).
export const ANALOG_PORTS = [
  'analog_1', 'analog_2', 'analog_3', 'analog_4',
  'impulse_counter_1', 'impulse_counter_2',
  'lls_temperature_1', 'lls_temperature_2', 'lls_level_1', 'lls_level_2',
  'board_voltage', 'can_engine_temp', 'can_mileage', 'can_throttle', 'can_fuel',
  'can_fuel_litres', 'can_consumption', 'can_rpm', 'can_speed', 'can_engine_load', 'can_fuel_rate',
  'raw_can_1', 'raw_can_2', 'raw_can_3', 'raw_can_4', 'raw_can_5',
  'raw_can_6', 'raw_can_7', 'raw_can_8', 'raw_can_9', 'raw_can_10',
  'can_axle_load_1', 'can_axle_load_2', 'can_axle_load_3', 'can_axle_load_4', 'can_axle_load_5',
  'temp_sensor', 'ext_temp_sensor_1', 'ext_temp_sensor_2', 'ext_temp_sensor_3',
] as const
const ANALOG_TYPES = ['rpm', 'fuel', 'fuel_consumption', 'temperature', 'externalPower'] as const
const ANALOG_UNITS = ['litre', 'percent', 'celsius', 'millivolt', 'rpm'] as const

// Form shape, decoupled from the wire AnalogPortConfig — port/type/unit come
// from Selects; min/max/multiplier are text (mapped to number|null by the tab).
const buildSchema = (requiredMessage: string) =>
  z.object({
    port: z.string().min(1, requiredMessage),
    type: z.string().min(1, requiredMessage),
    unit: z.string().min(1, requiredMessage),
    multiplier: z.string(),
    min: z.string(),
    max: z.string(),
    noise: z.boolean(),
  })

export type AnalogInputFormValues = z.infer<ReturnType<typeof buildSchema>>

export function AnalogInputEditForm({
  availablePorts,
  initialValues,
  onSubmit,
  onCancel,
}: {
  availablePorts: string[]
  initialValues?: AnalogInputFormValues
  onSubmit: SubmitHandler<AnalogInputFormValues>
  onCancel: () => void
}) {
  const { t } = useTranslation('feature')
  const { t: tCommon } = useTranslation('common')
  const schema = useMemo(() => buildSchema(t('analogInputForm.required')), [t])
  const { control, handleSubmit, formState } = useForm<AnalogInputFormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues ?? { port: '', type: '', unit: '', multiplier: '', min: '', max: '', noise: false },
  })

  return (
    <form className="flex flex-col gap-4 rounded-md border p-4" onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="port"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel>{t('analogInputForm.port')}</FieldLabel>
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger aria-label={t('analogInputForm.port')}>
                <SelectValue placeholder={tCommon('ui.label.select')} />
              </SelectTrigger>
              <SelectContent>
                {availablePorts.map((port) => (
                  <SelectItem key={port} value={port}>
                    {t(`trackingObjects.config.analogPorts.${port}` as 'trackingObjects.config.analogPorts.analog_1')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="type"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel>{t('analogInputForm.type')}</FieldLabel>
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger aria-label={t('analogInputForm.type')}>
                <SelectValue placeholder={tCommon('ui.label.select')} />
              </SelectTrigger>
              <SelectContent>
                {ANALOG_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(`trackingObjects.config.analogInputTypes.${type}` as 'trackingObjects.config.analogInputTypes.rpm')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="unit"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel>{t('analogInputForm.unit')}</FieldLabel>
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger aria-label={t('analogInputForm.unit')}>
                <SelectValue placeholder={tCommon('ui.label.select')} />
              </SelectTrigger>
              <SelectContent>
                {ANALOG_UNITS.map((unit) => (
                  <SelectItem key={unit} value={unit}>
                    {t(`trackingObjects.config.analogInputUnits.${unit}` as 'trackingObjects.config.analogInputUnits.litre')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="multiplier"
        render={({ field }) => (
          <Field>
            <FieldLabel htmlFor="multiplier">{t('analogInputForm.multiplier')}</FieldLabel>
            <Input id="multiplier" type="number" step="any" {...field} />
          </Field>
        )}
      />

      <div className="flex gap-3">
        <Controller
          control={control}
          name="min"
          render={({ field }) => (
            <Field>
              <FieldLabel htmlFor="min">{t('analogInputForm.min')}</FieldLabel>
              <Input id="min" type="number" step="any" {...field} />
            </Field>
          )}
        />
        <Controller
          control={control}
          name="max"
          render={({ field }) => (
            <Field>
              <FieldLabel htmlFor="max">{t('analogInputForm.max')}</FieldLabel>
              <Input id="max" type="number" step="any" {...field} />
            </Field>
          )}
        />
      </div>

      <Controller
        control={control}
        name="noise"
        render={({ field }) => (
          <label className="flex items-center gap-2 text-sm font-medium">
            <Checkbox checked={field.value} onCheckedChange={(c) => field.onChange(c === true)} />
            {t('analogInputForm.noise')}
          </label>
        )}
      />

      <div className="flex justify-between gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('analogInputForm.cancel')}
        </Button>
        <Button type="submit" disabled={formState.isSubmitting}>
          {formState.isSubmitting ? t('analogInputForm.submitting') : t('analogInputForm.submit')}
        </Button>
      </div>
    </form>
  )
}
